import { Component, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { constants } from '../../../../constants';
import * as d3 from 'd3';
import {
  DataFilters,
  DataFiltersService,
} from '../../services/data-filters.service';
import { StatisticsService } from '../../services/statistics.service';
import { Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import { SizeOfPrizeTop } from '../../models/statistics.model';
import { AuthService } from '../../../auth/services/auth.service';

@Component({
  selector: 'mpro-top-consumer',
  templateUrl: './top-consumer.component.html',
  styleUrls: ['./top-consumer.component.scss'],
})
export class TopConsumer implements OnInit, OnChanges, OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject();
  public currentYear: number = new Date().getFullYear();
  public legendItems = constants.legendItems;
  public dataFilters: DataFilters = new DataFilters();
  public constants = constants;
  public activeSorting: string = 'risers';
  public tableData: SizeOfPrizeTop[] = [];
  public top10ConsumerContinents: any[] = [];
  public isFreemium: boolean;

  constructor(
    private dataFiltersService: DataFiltersService,
    private statisticsService: StatisticsService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.authService.permissions$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((plan) => (this.isFreemium = plan.isFreemium));
    this.dataFiltersService
      .getDataFilters()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((dataFilters) => {
        this.dataFilters = dataFilters;
        this.statisticsService.getSizeOfPrizeTop$.next();
      });
    this.getStatisticsConsumerSpending();
  }

  public ngOnChanges() {
    this.handleSortingData(this.activeSorting);
  }

  private getStatisticsConsumerSpending() {
    this.statisticsService.getSizeOfPrizeTop$
      .pipe(
        takeUntil(this.ngUnsubscribe),
        debounceTime(300),
        switchMap(() => {
          return this.statisticsService.getSizeOfPrizeTop();
        })
      )
      .subscribe((response) => {
        this.tableData = response;
        this.handleSortingData(this.activeSorting);
      });
  }

  public handleSortingData(activeSorting: string) {
    this.activeSorting = activeSorting;
    const tableData = [...this.tableData].sort((a, b) => b.value - a.value);
    activeSorting === 'risers' ? tableData : tableData.reverse();
    this.destroyChart();
    if (this.isFreemium) {
      const top10ConsumerContinents = tableData
        .slice(0, 10)
        .map((continent) => ({
          ...continent,
          color: constants.legendItems.find(
            (continentColor) => continentColor.text === continent.continentName
          )?.color,
        }));
      this.top10ConsumerContinents = top10ConsumerContinents;
    } else {
      const top10ConsumerContinents = tableData.map((continent) => ({
        ...continent,
        color: constants.legendItems.find(
          (continentColor) => continentColor.code === continent.continentCode
        )?.color,
      }));
      this.top10ConsumerContinents = top10ConsumerContinents;
    }
    this.drawChart();
  }

  public destroyChart() {
    d3.select('#horizontalBarChart').selectAll('svg').remove();
  }

  protected drawChart() {
    const horizontalBarChartContainer: HTMLDivElement | null =
      document.querySelector('#horizontalBarChart');

    const width = horizontalBarChartContainer?.clientWidth;
    const height = 35 * this.tableData.length;
    const margin = { top: 20, right: 20, bottom: 0, left: 70 };

    // append the svg object to the body of the page
    const svg = d3
      .select('#horizontalBarChart')
      .append('svg')
      .attr('width', width! + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    // Parse the Data
    const largestContinent = this.top10ConsumerContinents
      .slice()
      .sort((a, b) => b.value - a.value)[0].value;

    // Add X axis
    const x = d3
      .scaleLinear()
      .domain([0, Math.ceil(largestContinent / 10) * 10])
      .range([0, width!]);

    //for x axis

    svg
      .append('g')
      .attr('class', 'xAxis')
      .call(d3.axisTop(x))
      .attr('transform', `translate(0, 0)`);

    // Y axis
    const y = d3
      .scaleBand()
      .range([0, height])
      .domain(this.top10ConsumerContinents.map((d) => d.continentName))
      .padding(0.4);

    svg.append('g').attr('class', 'yAxis').call(d3.axisLeft(y));
    // After yAxis creation, select all yAxis text elements to manipulate their appearance
    svg.selectAll('.yAxis text').each(function () {
      const text = d3.select(this);
      const fullText = text.text();
      // Check if the text contains a space indicating more than one word i.e South America
      if (fullText.includes(' ')) {
        const firstSpaceIndex = fullText.indexOf(' '); // Find index of first space
        const firstWord = fullText.substring(0, firstSpaceIndex); // Get first word
        const remainingText = fullText.substring(firstSpaceIndex + 1); // Get remaining text
        // Clear the original text and setup the two lines
        text.text(''); // Clear the text
        text
          .append('tspan') // First line for the first word
          .attr('x', '-1.0em')
          .attr('dy', '-0.6em') //Positioning
          .text(firstWord);
        text
          .append('tspan') // Second line
          .attr('x', '-1.0em')
          .attr('dy', '1.6em') // Line spacing
          .text(remainingText);
      }
    });

    d3.selectAll('g.yAxis g.tick')
      .append('line')
      .attr('class', 'gridline')
      .attr('x1', -300)
      .attr('y1', 20)
      .attr('x2', width!)
      .attr('y2', 20)
      .attr('stroke', '#E4E4E4');

    d3.selectAll('g.xAxis g.tick')
      .append('line')
      .attr('class', 'gridline')
      .attr('x1', 0)
      .attr('y1', height)
      .attr('x2', 0)
      .attr('y2', 0)
      .attr('stroke', '#E4E4E4');

    //Bars
    svg
      .selectAll('myRect')
      .data(this.top10ConsumerContinents)
      .join('rect')
      .attr('x', x(0))
      .attr('y', (d) => y(d.continentName)!)
      .attr('width', (d) => x(d.value))
      .attr('height', y.bandwidth())
      .attr('fill', (d) => d.color!);
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
